import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Plus,
  Trash2,
  Clock,
  CalendarCheck,
  CalendarX,
  Music,
} from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, addDoc, deleteDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';

export default function AdminCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [blockedDates, setBlockedDates] = useState([]);
  const [shows, setShows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  // Fetch blocked dates from Firestore
  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'blockedDates'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dates = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlockedDates(dates);
    });

    return unsubscribe;
  }, []);

  // Fetch shows
  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'shows'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const showsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShows(showsData);
    });

    return unsubscribe;
  }, []);

  // Fetch bookings
  useEffect(() => {
    if (!db) return;

    const q = query(collection(db, 'bookings'), orderBy('eventDate', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const bookingsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBookings(bookingsData);
    });

    return unsubscribe;
  }, []);

  // Calendar calculations
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, [currentDate]);

  const firstDayOfMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month, 1).getDay();
  }, [currentDate]);

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date for comparison
  const formatDateString = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Check if date has events
  const getDateStatus = (day) => {
    const dateStr = formatDateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));

    const isBlocked = blockedDates.some((b) => b.date === dateStr);
    const hasShow = shows.some((s) => s.date === dateStr && s.status !== 'cancelled');
    const hasBooking = bookings.some((b) => b.eventDate === dateStr && b.status === 'confirmed');
    const hasPendingBooking = bookings.some((b) => b.eventDate === dateStr && b.status === 'pending');

    return { isBlocked, hasShow, hasBooking, hasPendingBooking };
  };

  // Get events for selected date
  const getDateEvents = (dateStr) => {
    const dayShows = shows.filter((s) => s.date === dateStr);
    const dayBookings = bookings.filter((b) => b.eventDate === dateStr);
    const dayBlocked = blockedDates.find((b) => b.date === dateStr);

    return { shows: dayShows, bookings: dayBookings, blocked: dayBlocked };
  };

  const handleDayClick = (day) => {
    const dateStr = formatDateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
    setSelectedDate(dateStr);
    setIsModalOpen(true);
  };

  const handleBlockDate = async () => {
    if (!selectedDate) return;
    setIsLoading(true);

    try {
      await addDoc(collection(db, 'blockedDates'), {
        date: selectedDate,
        reason: blockReason || 'Unavailable',
        createdAt: new Date(),
      });
      setBlockReason('');
    } catch (error) {
      console.error('Error blocking date:', error);
      alert('Failed to block date');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnblockDate = async (blockedId) => {
    try {
      await deleteDoc(doc(db, 'blockedDates', blockedId));
    } catch (error) {
      console.error('Error unblocking date:', error);
      alert('Failed to unblock date');
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    return checkDate < today;
  };

  // Stats
  const stats = {
    upcomingShows: shows.filter((s) => new Date(s.date) >= new Date() && s.status === 'upcoming').length,
    confirmedBookings: bookings.filter((b) => b.status === 'confirmed').length,
    pendingBookings: bookings.filter((b) => b.status === 'pending').length,
    blockedDays: blockedDates.length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Calendar Manager</h2>
          <p className="text-gray-400">Manage availability and view scheduled events</p>
        </div>
        <Button onClick={goToToday} variant="outline">
          <Calendar className="w-4 h-4 mr-2" />
          Today
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Music className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.upcomingShows}</p>
                <p className="text-xs text-gray-400">Upcoming Shows</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <CalendarCheck className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.confirmedBookings}</p>
                <p className="text-xs text-gray-400">Confirmed Bookings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pendingBookings}</p>
                <p className="text-xs text-gray-400">Pending Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <CalendarX className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.blockedDays}</p>
                <p className="text-xs text-gray-400">Blocked Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-xl">{monthName}</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={previousMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month starts */}
            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square" />
            ))}

            {/* Days of the month */}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const status = getDateStatus(day);
              const past = isPast(day);

              return (
                <motion.button
                  key={day}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleDayClick(day)}
                  disabled={past}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all
                    ${isToday(day) ? 'ring-2 ring-orange-500' : ''}
                    ${past ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-800'}
                    ${status.isBlocked ? 'bg-red-500/20' : ''}
                    ${status.hasShow ? 'bg-green-500/20' : ''}
                    ${status.hasBooking ? 'bg-blue-500/20' : ''}
                    ${status.hasPendingBooking && !status.hasBooking ? 'bg-yellow-500/20' : ''}
                  `}
                >
                  <span className={`text-sm font-medium ${isToday(day) ? 'text-orange-500' : 'text-white'}`}>
                    {day}
                  </span>

                  {/* Status indicators */}
                  <div className="flex gap-0.5 mt-1">
                    {status.hasShow && (
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Show scheduled" />
                    )}
                    {status.hasBooking && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" title="Booking confirmed" />
                    )}
                    {status.hasPendingBooking && !status.hasBooking && (
                      <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" title="Pending booking" />
                    )}
                    {status.isBlocked && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="Blocked" />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-gray-800">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-3 h-3 rounded-full bg-green-500" />
              Shows
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-3 h-3 rounded-full bg-blue-500" />
              Confirmed Bookings
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-3 h-3 rounded-full bg-yellow-500" />
              Pending Bookings
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              Blocked
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Date Detail Modal */}
      {isModalOpen && selectedDate && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-900 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-gray-900 p-6 border-b border-gray-800 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </h3>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {(() => {
                const events = getDateEvents(selectedDate);
                return (
                  <>
                    {/* Shows */}
                    {events.shows.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                          <Music className="w-4 h-4" />
                          Shows
                        </h4>
                        {events.shows.map((show) => (
                          <div key={show.id} className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg mb-2">
                            <p className="font-medium text-white">{show.title}</p>
                            <p className="text-sm text-gray-400">{show.venue} - {show.city}, {show.state}</p>
                            {show.time && <p className="text-sm text-green-400">{show.time}</p>}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Bookings */}
                    {events.bookings.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                          <CalendarCheck className="w-4 h-4" />
                          Bookings
                        </h4>
                        {events.bookings.map((booking) => (
                          <div
                            key={booking.id}
                            className={`p-3 rounded-lg mb-2 border ${
                              booking.status === 'confirmed'
                                ? 'bg-blue-500/10 border-blue-500/30'
                                : booking.status === 'pending'
                                ? 'bg-yellow-500/10 border-yellow-500/30'
                                : 'bg-gray-800 border-gray-700'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-white">{booking.eventType}</p>
                              <span
                                className={`px-2 py-0.5 text-xs font-semibold rounded capitalize ${
                                  booking.status === 'confirmed'
                                    ? 'bg-blue-500 text-white'
                                    : booking.status === 'pending'
                                    ? 'bg-yellow-500 text-black'
                                    : 'bg-gray-600 text-white'
                                }`}
                              >
                                {booking.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-400">{booking.contactName}</p>
                            <p className="text-sm text-gray-400">{booking.venue}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Blocked Status */}
                    {events.blocked ? (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                          <CalendarX className="w-4 h-4" />
                          Blocked
                        </h4>
                        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white">Date is blocked</p>
                            <p className="text-sm text-gray-400">{events.blocked.reason || 'No reason provided'}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUnblockDate(events.blocked.id)}
                            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Unblock
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3 flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          Block This Date
                        </h4>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="blockReason">Reason (optional)</Label>
                            <Input
                              id="blockReason"
                              value={blockReason}
                              onChange={(e) => setBlockReason(e.target.value)}
                              placeholder="e.g., Personal day, Travel, etc."
                            />
                          </div>
                          <Button
                            onClick={handleBlockDate}
                            disabled={isLoading}
                            className="w-full bg-red-500 hover:bg-red-600"
                          >
                            <CalendarX className="w-4 h-4 mr-2" />
                            {isLoading ? 'Blocking...' : 'Block Date'}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* No events message */}
                    {events.shows.length === 0 && events.bookings.length === 0 && !events.blocked && (
                      <div className="text-center py-4">
                        <CalendarCheck className="w-12 h-12 text-green-500 mx-auto mb-2" />
                        <p className="text-gray-400">This date is available for bookings</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
